<template>
	<div class="card-page">
		<ja-header ref="header" title="JA Finance Park">
			<template v-slot:icon>
				<svgicon name="headerIcons/home"></svgicon>
			</template>
		</ja-header>

		<content>
			<div class="wrapper">
				<legend class="heading-1 question-box" aria-live="polite">
					{{ $t('reports') }}
				</legend>
				<div v-if="simulations.length" class="simulations-list">
					<ja-card
						v-for="simulation in simulations"
						:key="simulation.sid"
						@click.native="openReport(simulation)"
					>
						<div class="card-content">
							<div class="simulation__name">
								<span class="text--block">{{ simulation.sessionName }}</span>
								<span class="text--muted"
									>{{ $t('try') }} {{ simulation.simulationTry }}</span
								>
							</div>
							<div
								v-if="simulation.dateOfSimulationCompletion"
								class="simulation__date"
							>
								<span class="text--block text--upper text--muted">
									{{ $t('endDate') }}
								</span>
								<span>
									{{
										simulation.dateOfSimulationCompletion | dateFormatNumeric
									}}
								</span>
							</div>
						</div>
					</ja-card>
					<ja-pagination
						:data-per-page="recordsPerPage"
						:data-count="simulationsCount"
						@changePage="updatePagination($event)"
					>
					</ja-pagination>
				</div>

				<div class="btn-group btn-group--center">
					<ja-button
						name="cancel"
						class="large outlined"
						@click.native="cancel"
					>
						{{ $t('cancel') }}
					</ja-button>
				</div>
			</div>
		</content>
	</div>
</template>

<script>
import Card from '@/components/ui/Card';
import Header from '@ui/headers/Header';
import Button from '@ui/Button';
import gql from 'graphql-tag';
import { UserData, ResetLifeStageAndCategories } from '@/mixins';
import Pagination from '@/components/AdminSection/elements/Pagination';
import {
	setStorageReportSim,
	removeStorageReportSim,
	removeStorageRedirectTo,
	swapChooseSimulation
} from '@/helpers/storageFunctions';

export default {
	components: {
		jaCard: Card,
		jaHeader: Header,
		jaButton: Button,
		jaPagination: Pagination
	},
	mixins: [UserData, ResetLifeStageAndCategories],
	data() {
		return {
			selectedSim: {},
			simulations: [],
			currentPage: 1,
			recordsPerPage: 10,
			simulationsCount: 0,
			skipQuery: false
		};
	},
	computed: {
		filters() {
			const criteria = [];
			if (this.userData) {
				criteria.push(`userId=${this.userData.userId}`);
				criteria.push('isSimulationCompleted = "true"');
				criteria.push('simulation != null');
			}

			return criteria.join('&&');
		}
	},
	mounted() {
		this.$store.commit('setLoading', true);
	},
	apollo: {
		students: {
			query: gql`
				query simulations($where: String!, $skip: Int!, $take: Int!) {
					students(
						where: $where
						skip: $skip
						take: $take
						order: "dateOfSimulationCompletion desc"
					) {
						simulationId
						id
						simulationTry
						isSimulationCompleted
						dateOfSimulationCompletion
						simulation {
							sessionName
							endDate
						}
					}
					simCount: getCountStudent(where: $where)
				}
			`,
			fetchPolicy: 'no-cache',
			variables() {
				return {
					where: this.filters,
					skip: this.recordsPerPage * (this.currentPage - 1),
					take: this.recordsPerPage
				};
			},
			update(data) {
				this.simulationsCount = data.simCount || 0;
				this.simulations = data.students.flatMap(element => {
					return {
						sessionName: element.simulation.sessionName,
						id: element.simulationId,
						sid: element.id,
						endDate: element.simulation.endDate,
						dateOfSimulationCompletion: element.dateOfSimulationCompletion,
						simulationTry: element.simulationTry
					};
				});

				this.$store.commit('setLoading', false);
			},
			skip() {
				return this.skipQuery || !this.userData;
			}
		}
	},
	methods: {
		cancel() {
			// TODO: REDIRECT USER BACK
			this.$store.commit('setLoading', true);

			swapChooseSimulation();

			// removeStorageRecirectTo with param true will return the value before deletion
			const redirectBackTo = removeStorageRedirectTo(true) || 'home';

			if (redirectBackTo === 'home' || redirectBackTo === 'category') {
				this.resetLifeStageAndCategories();
			}

			this.$router.push({ name: redirectBackTo }).catch(e => e);
		},
		updatePagination(page) {
			this.currentPage = page === 0 ? 1 : page;
		},
		openReport(simulation) {
			setStorageReportSim(simulation);

			this.$store.dispatch('reset').then(() => {
				this.$store.commit('setLoading', true);
				this.skipQuery = true;

				this.$router.push({ name: 'studentSummary' });

				this.$store.dispatch('getConstants');
			});
		}
	},
	beforeRouterLeave(to, from, next) {
		removeStorageReportSim();
		next();
	}
};
</script>

<style lang="scss" scoped>
content {
	width: 100%;
	overflow-y: auto;
	flex-grow: 1;
	padding-top: 32px;

	.btn-group {
		margin-top: 52px;
	}

	.wrapper {
		width: 100%;
		max-width: 720px;
		margin: auto;
		margin-bottom: 48px;

		.question-box {
			text-align: center;
		}
	}
}

.question-box {
	margin-top: 0;
	margin-bottom: 32px;
	outline: none;
}

.card-content {
	display: flex;
	justify-content: space-between;
	align-items: center;

	.simulation__date {
		text-align: right;
	}
}
</style>
